import React, { useEffect, useState } from 'react'
import {
  Control,
  FieldValues,
  UseFormSetValue,
  Path,
  Controller,
  useWatch
} from 'react-hook-form'

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Switch,
  Textfield
} from '@clientbase/clientbase-library'
import { Box, Grid, Link, Typography } from '@mui/material'
import { BillingRule } from 'services/BillingRule'

import { useSessionCookies } from 'components/CompanySelector/CompanySessionProvider'

export interface NegativationProps<T extends FieldValues> {
  control: Control<T, object>
  setValue?: UseFormSetValue<T>
}

const Negativation = <T extends FieldValues>({
  control,
  setValue
}: NegativationProps<T>) => {
  // SC-11742: o prazo padrão vem da régua de cobrança (área "Negativação") e os
  // avisos antes da negativação são as notificações extras de lá. A cobrança só
  // mostra o que vai receber; não há configuração de aviso por cobrança.
  const { ['nextauth.default_days_to_report']: defaultDaysCookie } =
    useSessionCookies()
  const defaultDays = defaultDaysCookie || '30'
  const daysToReport = useWatch({ control, name: 'daysToReport' as Path<T> })
  const interestNegativation = useWatch({
    control,
    name: 'interestNegativation' as Path<T>
  })
  const [warnings, setWarnings] = useState<string[] | null>(null)

  useEffect(() => {
    if (!interestNegativation || warnings !== null) return

    BillingRule.getConfigurationsNotifications().then((response) => {
      const customized = response?.record?.customized
      const extras =
        customized?.status === 'active'
          ? customized?.notification_rules?.negativation?.extra_notifications ??
            []
          : []
      setWarnings(
        extras
          .filter((extra: any) => extra?.notification_rules?.is_active)
          .map((extra: any) => extra.name)
      )
    })
  }, [interestNegativation, warnings])

  return (
    <Controller
      control={control}
      name={'interestNegativation' as Path<T>}
      render={({ field }) => (
        <Accordion
          expanded={field.value}
          onChange={(_, expanded) =>
            !!setValue &&
            setValue('interestNegativation' as Path<T>, expanded as any)
          }
          sx={{
            border: '1px solid #E8EBE8',
            borderLeft: '3px solid',
            borderLeftColor: 'primary.main',
            borderRadius: '8px !important',
            backgroundColor: '#FAFBFA',
            boxShadow: 'none',
            '&:before': { display: 'none' },
            '&.Mui-expanded': {
              margin: 0,
              backgroundColor: '#fff'
            }
          }}
        >
          <AccordionSummary
            hasSwitch
            expandIcon={<Switch checked={field.value} {...field} />}
          >
            <Typography variant="smBold">Negativar</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 2 }}>
            <Grid container={true} rowSpacing={1} columnSpacing={2}>
              <Grid
                item={true}
                xs={12}
                sm={12}
                display="flex"
                gap="8px"
                alignItems="center"
              >
                <Typography>Negativar automaticamente</Typography>
                <Box maxWidth="100px">
                  <Controller
                    control={control}
                    name={'daysToReport' as Path<T>}
                    render={({
                      field: { value, ref, ...field },
                      fieldState: { error }
                    }) => (
                      <Textfield
                        placeholder="30"
                        type="number"
                        inputRef={ref}
                        value={value}
                        helperText={error?.message}
                        error={!!error?.message}
                        {...field}
                      />
                    )}
                  />
                </Box>
                <Typography>dias após o vencimento da fatura</Typography>
              </Grid>
              <Grid item={true} xs={12}>
                <Typography variant="xsLight" color="#A3A6A2">
                  {String(daysToReport) === String(defaultDays) ? (
                    `Prazo padrão da régua de cobrança (${defaultDays} dias).`
                  ) : (
                    <>
                      Diferente do padrão da régua ({defaultDays} dias).{' '}
                      {setValue && (
                        <Link
                          component="button"
                          type="button"
                          variant="xsLight"
                          onClick={() =>
                            setValue(
                              'daysToReport' as Path<T>,
                              defaultDays as any,
                              { shouldValidate: true }
                            )
                          }
                        >
                          Usar o padrão
                        </Link>
                      )}
                    </>
                  )}
                </Typography>
              </Grid>
              {warnings !== null && (
                <Grid item={true} xs={12}>
                  <Typography variant="xsLight" color="#646664">
                    {warnings.length > 0
                      ? `Antes da negativação, o cliente recebe ${
                          warnings.length === 1
                            ? 'o aviso'
                            : `os ${warnings.length} avisos`
                        } da régua de cobrança: ${warnings.join(
                          ', '
                        )}. Cada envio é cobrado como notificação extra.`
                      : 'Nenhum aviso antes da negativação está configurado na régua de cobrança.'}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </AccordionDetails>
        </Accordion>
      )}
    />
  )
}

export default Negativation
